import {useEffect, useState} from "react";
import AbreiseSelect from "./FindImmobilieForm/AbreiseSelect";
import AnreiseSelect from "./FindImmobilieForm/AnreiseSelect";
import MainFilter from "./FindImmobilieForm/MainFilter";
import PersonenSelect from "./FindImmobilieForm/PersonenSelect";
import {NextRouter, useRouter} from "next/router";
import {enforceArray, replaceRouterQueryParamsShallow} from "../helpers/FilterFunctions";
import Button from "./common/Button";
import {useTranslation} from "next-i18next";
import IdSearchField from "./common/IdSearchField";
import CustomListbox, {Item} from "./common/CustomListbox";
import {Filter} from "../data/Filter/Filter.config";
import {useQuery} from "@tanstack/react-query";
import {fetchItem} from "../data/ReactQueries";
import {cities} from "../models/cities";

export default function VermietungMainFilter() {
	const {data: activeCities, isLoading: activeCitiesLoading} = useQuery(
		["activeCities"],
		(): Promise<cities[]> => {
			return fetchItem("cities", {});
		},
		{initialData: []}
	);

	const {t} = useTranslation();
	const [personCount, setPersonCount] = useState<number | undefined>();
	const [startDate, setStartDate] = useState<Date | null>();
	const [endDate, setEndDate] = useState<Date | null>();
	const router = useRouter();
	const [objectIdSearchVal, setObjectIdSearchVal] = useState<string>(router.query.id as string);
	const [selectedCities, setSelectedCities] = useState<string[]>();
	const [defaultSelectedCities, setDefaultSelectedCities] = useState<Item[]>();

	function handlePersonCountChange(personCount: number) {
		if (personCount > 0) {
			setPersonCount(personCount);
		} else {
			setPersonCount(undefined);
		}
	}

	useEffect(() => {
		if (router.isReady) {
			const personCountFromQuery = enforceArray(router.query.min_persons_total);
			const fromDate = enforceArray(router.query.from_date);
			const toDate = enforceArray(router.query.to_date);
			const cities = enforceArray(router.query[Filter.City.queryKey]);

			if (cities) {
				setDefaultSelectedCities(
					activeCities
						.filter((city) =>
							enforceArray(router.query[Filter.City.queryKey]).includes(
								city.id.toString()
							)
						)
						.map((city) => {
							return {name: city.name};
						})
				);
			}
			if (
				personCountFromQuery &&
				personCountFromQuery.length > 0 &&
				personCountFromQuery[0]
			) {
				setPersonCount(parseInt(personCountFromQuery[0]));
			}
			if (fromDate[0] && toDate[0]) {
				setStartDate(new Date(fromDate[0] * 1000));
				setEndDate(new Date(toDate[0] * 1000));
			}
		}
	}, [router.isReady, activeCities]);

	useEffect(() => {
		if (
			router.isReady &&
			!router.query.from_date &&
			!router.query.to_date &&
			!router.query.min_persons_total
		) {
			setEndDate(undefined);
			setStartDate(undefined);
			setPersonCount(undefined);
		}
	}, [
		router.query.from_date,
		router.query.to_date,
		router.query.min_persons_total
	]);

	function handleFilterChange(
		router: NextRouter,
		startDateAnreise?: Date,
		startDateAbreise?: Date,
		personCount?: number,
		searchedObjectID?: string,
		cities?: string[]
	) {
		let queryParams = router.query;
		if (cities) {
			const cityIds = activeCities
				.filter((city) => cities.includes(city.name))
				.map((city) => city.id.toString());
			queryParams = {...queryParams, [Filter.City.queryKey]: cityIds};
		}
		if (startDateAnreise && startDateAbreise) {
			const anreiseUnixStamp = Math.floor(startDateAnreise.getTime() / 1000);
			const abreiseUnixStamp = Math.floor(startDateAbreise.getTime() / 1000);
			queryParams = {
				...queryParams,
				from_date: anreiseUnixStamp.toString(),
				to_date: abreiseUnixStamp.toString()
			};
		}
		if (personCount && personCount > 0) {
			queryParams = {
				...queryParams,
				min_persons_total: personCount.toString()
			};
		} else {
			delete queryParams.min_persons_total;
		}
		if (searchedObjectID) {
			queryParams = {
				...queryParams,
				id: searchedObjectID
			};
		} else {
			delete queryParams.id;
		}
		replaceRouterQueryParamsShallow(router, queryParams);

		if (typeof window !== "undefined") {
			setTimeout(() => {
				document
					.getElementById("search-result-container")
					?.scrollIntoView({behavior: "smooth"});
			}, 500);
		}
	}

	const submit = () => {
		handleFilterChange(
			router,
			startDate ?? undefined,
			endDate ?? undefined,
			personCount,
			objectIdSearchVal,
			selectedCities
		);
	}
	return (
		<div className="container mx-auto relative">
			<div className="lg:w-11/12 xl:w-10/12 ml-auto mr-auto lg:-mt-10">
				<MainFilter>
					<AnreiseSelect
						onStartDateChange={setStartDate}
						onEndDateChange={setEndDate}
						startDate={startDate}
						endDate={endDate}
					/>
					<AbreiseSelect
						onStartDateChange={setStartDate}
						onEndDateChange={setEndDate}
						startDate={startDate}
						endDate={endDate}
					/>
					<PersonenSelect
						personCount={personCount}
						onChangePersonCount={(personCount) => {
							handlePersonCountChange(personCount);
						}}
						onSubmit={() => submit()}
						isVermietungMainFilter={true}
					/>
					<div className="space-y-2 col-span-1">
            <span className="text-[16px] md:text-[20px]">
              {t("components.immobilieKaufen.city")}
            </span>
						<CustomListbox
							items={enforceArray(activeCities).map((city) => {
								return {name: city.name};
							})}
							onSelectItems={(items) => {
								setSelectedCities(items.map((i) => i.name));
							}}
							defaultChecked={defaultSelectedCities}
						/>
					</div>
					<IdSearchField title={t("common.objectidlabel")} onChange={setObjectIdSearchVal} onSubmit={() => submit()} isVermietungMainFilter={true}/>
					<div
						className="col-span-2 lg:col-span-1 cursor-pointer space-y-2"
						onClick={() => {
							submit()
						}}
					>
						<span className="text-[16px]">&nbsp;</span>
						<Button
							padding="px-0"
							text={t("common.findObject")}
							transparent={false}
							border={false}
							height={"h-[40px]"}
							className="rounded-[10px] font-bold"
							bgColor="min-w-[140px] bg-gradient-to-b from-[#a8e1f7] to-[#43c4f7] hover:bg-leo-dark-brown hover:from-transparent hover:to-transparent transition-all duration-300"
						/>
					</div>
				</MainFilter>
			</div>
		</div>
	);
}
